import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  CardFooter,
  Badge,
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Helmet from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { useFetch, PseudoStates, Button, Field, FormAlerts } from '@cordata/buffet';
import { Formik } from 'formik';
import { sortBy } from 'lodash';
import { parse, format } from 'date-fns';

import { authorizedInstance as axios, formatPhone } from '~/utils';
import OtherUser from '~/assets/other-user.png';

import { useBreakpoint } from '~/utils/breakpoint';

import PriorityNote from './PriorityNote';

import ChangeProfilePicture from './ChangeProfilePicture';

type tPatientsBannerProps = {
  full: boolean;
  refresh: {
    badges?: any;
  };
};

const PatientsBanner = ({
  full,
  refresh = {},
}: tPatientsBannerProps): JSX.Element => {
  const params = useParams();
  const [status, data] = useFetch(`/patients/${params.patient_id}`);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { md, xl } = useBreakpoint();

  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfileFullOpen, setIsProfileFullOpen] = useState(false);
  const [episodeStatuses, setEpisodeStatuses] = useState([]);
  const [badges, setBadges] = useState([]);

  const toggleIsProfileFullOpen = () =>
    setIsProfileFullOpen(p => !p);

  const toggleIsProfileOpen = () =>
    setIsProfileOpen(p => !p);

  const fetchEpisodeStatuses = () => axios
    .get('/reference/reference-statuses?type=Episode')
    .then((r: any) => r.data.data)
    .then((r: any) => sortBy(r, ['attributes.defaultSortOrder', 'attributes.statusLabel:desc']))
    .then(setEpisodeStatuses);

  useEffect(() => {
    axios
      .get(`/patients/${params.patient_id}/patient-badges`)
      .then((r: any) => r.data.data)
      .then(setBadges);
  }, [refresh.badges]);

  const parseScreenSizeForImageSize = () => {
    if (xl) {
      return 89;
    } else if (md) {
      return 80;
    }

    return 60;
  };

  const hasPriorityNote = data && data.note !== null;

  return (
    <Card className="mb-3 border-primary">
      {status === 'success' ? (
        <>
          <CardBody className="p-0">
            <Helmet
              titleTemplate={data && `%s - ${data.lastName}, ${data.firstName} - Navigator`}
              defaultTitle={data && `${data.lastName}, ${data.firstName} - Navigator`}
            />

            <div className="media">
              <img role="presentation" onClick={toggleIsProfileFullOpen} src={data.personImage || OtherUser} alt="avatar" width={parseScreenSizeForImageSize()} height={parseScreenSizeForImageSize()} className="mr-2 bg-tertiary border-right border-primary rounded-left cursor-pointer" />

              <div className="media-body p-2">
                <CardTitle className="mb-1 d-flex justify-content-between align-items-center" tag="h4">
                  <span>
                    {data.lastName}, {data.firstName} {data.middleName}{' '}

                    {(full || md) && (
                      <small className="d-block d-sm-inline">
                        DOB: {data.birthDate ? format(parse(data.birthDate), 'MM/DD/YYYY') : 'Unknown'}
                        , Sex: {data.genderLabel}
                        {data.genderIdentityLabel && (
                          <>, Gender Identity: {data.genderIdentityLabel}</>
                        )}
                      </small>
                    )}
                  </span>

                  <span className="ml-3">
                    <UncontrolledButtonDropdown size="sm">
                      <Button
                        outline
                        color="tertiary"
                        tag={Link}
                        to={`/patients/${data.id}/demographics`}
                      >
                        <FontAwesomeIcon icon="cogs" />
                      </Button>

                      <DropdownToggle caret outline color="tertiary" />

                      <DropdownMenu right>
                        <DropdownItem
                          onClick={() => setIsModalOpen(!isModalOpen)}
                        >
                          Change Episode Status
                        </DropdownItem>

                        <DropdownItem
                          onClick={toggleIsProfileOpen}
                        >
                          Change Profile Picture
                        </DropdownItem>

                        <DropdownItem
                          onClick={() => setIsPriorityModalOpen(!isPriorityModalOpen)}
                        >
                          <FontAwesomeIcon icon={hasPriorityNote ? 'pen' : 'plus'} /> Priority Note
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledButtonDropdown>
                  </span>
                </CardTitle>

                {(full || md) && (
                  <>
                    <CardSubtitle>
                      {formatPhone(data.forDupsPreferredPhone)}{data.forDupsPreferredPhone && ' / '}{data.summaryPatient.episodeStatusLabel}
                    </CardSubtitle>

                    <div className="badge-list">
                      {sortBy(badges, [
                        'relationships.referenceBadgeGroup.attributes.defaultSortOrder',
                        'relationships.referenceBadgeGroup.attributes.badgeGroupLabel',
                        'relationships.referenceBadge.attributes.defaultSortOrder',
                        'relationships.referenceBadge.attributes.badgeLabel',
                      ]).map((b: any) => (
                        <Badge
                          key={b.id}
                          className="mr-1"
                          style={{
                            backgroundColor:
                              b.relationships.referenceBadge.attributes.color ||
                              'gray',
                          }}
                        >
                          {b.relationships.referenceBadge.attributes.badgeLabel}
                        </Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardBody>

          {hasPriorityNote && (
            <CardFooter className="p-2">
              <FontAwesomeIcon icon="exclamation-circle" className="mr-2 text-danger" />
              {data.note}
            </CardFooter>
          )}

          <PriorityNote
            isModalOpen={isPriorityModalOpen}
            setIsModalOpen={setIsPriorityModalOpen}
            data={data}
          />

          <ChangeProfilePicture isOpen={isProfileOpen} toggle={toggleIsProfileOpen} />

          <Modal isOpen={isProfileFullOpen} toggle={toggleIsProfileFullOpen}>
            <ModalHeader toggle={toggleIsProfileFullOpen}>Profile Picture</ModalHeader>

            <ModalBody>
              <img src={data.personImage || OtherUser} alt="avatar" width="100%" className="bg-tertiary border border-dark rounded" />
            </ModalBody>

            <ModalFooter>
              <Button
                type="cancel"
                outline
                onClick={toggleIsProfileFullOpen}
              >
                Cancel
              </Button>

              <Button
                color="primary"
                icon="cogs"
                onClick={() => {
                  toggleIsProfileFullOpen();
                  toggleIsProfileOpen();
                }}
              >
                Change Profile Picture
              </Button>
            </ModalFooter>
          </Modal>

          <Modal
            isOpen={isModalOpen}
            toggle={() => setIsModalOpen(!isModalOpen)}
            onEnter={() => fetchEpisodeStatuses()}
          >
            <Formik
              initialValues={{
                episodeStatus: {
                  id: data.summaryPatient.patientEpisodeUid,
                  attributes: { statusLabel: data.summaryPatient.episodeStatusLabel },
                },
              }}

              onSubmit={(values, { setErrors, setSubmitting }) =>
                axios
                  .put(`/patient-episodes/${data.summaryPatient.patientEpisodeUid}`, {
                    data: {
                      attributes: {
                        'new-status': values.episodeStatus.id,
                      },
                    },
                  })
                  .then(() => {
                    data.summaryPatient.episodeStatusLabel =
                      values.episodeStatus.attributes.statusLabel;

                    setIsModalOpen(!isModalOpen);
                  })
                  .catch(setErrors)
                  .then(() => setSubmitting(false))
              }
            >
              {({
                handleSubmit,
                isSubmitting,
                submitCount,
                errors,
                resetForm,
                dirty,
              }) => (
                <Form onSubmit={handleSubmit} noValidate>
                  <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
                    Change Episode Status
                  </ModalHeader>

                  <ModalBody>
                    { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

                    <Field
                      name="episodeStatus"
                      type="select"
                      options={episodeStatuses}
                      clearable={false}
                      getOptionValue={(o: any) => o.id}
                      getOptionLabel={(o: any) => o.attributes && o.attributes.statusLabel}
                    />
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      type="cancel"
                      outline
                      onClick={() => {
                        setIsModalOpen(!isModalOpen);
                        resetForm();
                      }}
                    >
                      Cancel Change
                    </Button>

                    <Button
                      type="submit"
                      disabled={
                        !dirty ||
                        isSubmitting
                      }
                    >
                      {isSubmitting ? 'Changing Status ...' : 'Change Status'}
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </Modal>
        </>
      ) : (
        <PseudoStates status={status} />
      )}
    </Card>
  );
};

export default PatientsBanner;
